/* eslint-disable max-lines, no-param-reassign */

import Joi from 'joi';
import set from 'lodash/set';

import isplainobject from 'lodash/isPlainObject';
import filter from 'lodash/filter';

export default class Velcro {
  constructor(schema, handlers = []) {
    this.schema = schema;
    this.handlers = handlers;
  }

  // parse values from url params
  // eg: converts a comma separated list into an array, "_" into an empty array
  // stringos false and true into actual booleans.
  parse(parameters) {
    const state = {};

    const handlers = filter(this.handlers, { runOn: 'parsePre' });

    const params = handlers.reduce((acc, def) => {
      const p = def.handler(acc);
      if (!p) {
        throw new Error(
          `A handler must always return an object ${def.handler.toString()}`,
        );
      }
      return p;
    }, parameters);

    Object.keys(params).forEach((name) => {
      let value = params[name];

      if (value === undefined) {
        return;
      }

      const path = this.getPath(name);
      const type = this.getType(name);

      if (type === 'array' && !Array.isArray(value)) {
        if (value === '_') {
          value = [];
        } else {
          value = value.split(',');
        }
      }

      if (type === 'boolean') {
        if (value === 'false' || value === 'no') {
          value = false;
        }

        if (value === 'true' || value === 'yes') {
          value = true;
        }
      }

      set(state, path, value);
    });
    return state;
  }

  validate(state, opt) {
    if (!state || typeof state !== 'object') {
      return { error: true };
    }

    return Joi.object().keys(this.schema).validate(state, opt);
  }

  getType(name) {
    const parts = filter(
      name.split('.'),
      (part) => !/^(0|[1-9]\d*)$/.test(part),
    );
    let currentSchema = this.schema[parts[0]];
    if (!currentSchema) {
      return null;
    }
    let { type } = currentSchema;
    parts.splice(0, 1); // eslint-disable-line prefer-destructuring

    while (parts.length > 0) {
      const currentPart = parts[0];
      if (type === 'array') {
        [currentSchema] = currentSchema.$_terms.items;
        type = currentSchema.type; // eslint-disable-line prefer-destructuring
      } else if (type === 'object') {
        /* eslint-disable no-underscore-dangle */
        const child = currentSchema._ids._byKey.get(currentPart);
        if (child) {
          currentSchema = child.schema;
          type = currentSchema.type; // eslint-disable-line prefer-destructuring
        } else {
          type = undefined;
        }

        parts.splice(0, 1);
      } else {
        type = undefined;

        parts.splice(0, 1);
      }
    }

    return type;
  }

  // eslint-disable-next-line class-methods-use-this
  getPath(name) {
    return name.replace(/(\.(\d+)\.)/g, '[$2].');
  }

  // eslint-disable-next-line class-methods-use-this
  getEncodedIfValueNotArray(field, value, encoded = []) {
    encoded[field] = value.sort().join(',');
    return encoded;
  }

  convertField(field, value) {
    let encoded = {};

    if (value === null) return encoded;

    if (Array.isArray(value)) {
      if (value.length) {
        if (!Array.isArray(value[0]) && !isplainobject(value[0])) {
          return this.getEncodedIfValueNotArray(field, value, encoded);
        }
        for (let i = 0; i < value.length; i++) {
          encoded = {
            ...encoded,
            ...this.convertField(`${field}.${i}`, value[i]),
          };
        }
        return encoded;
      }
      encoded[field] = '_';
      return encoded;
    }
    if (isplainobject(value) && !Object.keys(value).length) {
      encoded[field] = value;
      return encoded;
    }
    if (isplainobject(value)) {
      Object.keys(value).forEach((key) => {
        const encodedKeyValues = this.convertField(key, value[key]);

        Object.keys(encodedKeyValues).forEach((encodedKey) => {
          encoded[`${field}.${encodedKey}`] = encodedKeyValues[encodedKey];
        });
      });
      return encoded;
    }
    encoded[field] = value;
    return encoded;
  }

  // converts params into an object of diversal data represented as string
  // eg: convert arrays to comma separated lists, nested structures using dots (.), etc...
  convert(initialState) {
    const preHandlers = filter(this.handlers, { runOn: 'convertPre' });
    const postHandlers = filter(this.handlers, { runOn: 'convertPost' });

    const state = preHandlers.reduce((acc, def) => {
      const s = def.handler(acc);
      if (!s) {
        throw new Error(
          `A handler must always return an object ${def.handler.toString()}`,
        );
      }
      return s;
    }, initialState);

    let converted = {};
    Object.keys(state).forEach((key) => {
      converted = { ...converted, ...this.convertField(key, state[key]) };
    });

    return postHandlers.reduce((acc, def) => {
      const s = def.handler(acc);
      if (!s) {
        throw new Error(
          `A handler must always return an object ${def.handler.toString()}`,
        );
      }
      return s;
    }, converted);
  }

  getStateParametersForField(field, schema) {
    const { type } = schema;
    let parameters = [];

    if (type === 'object') {
      const keys = schema._inner.children; // eslint-disable-line no-underscore-dangle
      keys.forEach((key) => {
        const tmpParameters = this.getStateParametersForField(
          key.key,
          key.schema,
        );

        parameters = parameters.concat(
          this.prefixStateParameters(field, tmpParameters),
        );
      });
    } else if (type === 'array') {
      if (
        schema.$_terms.items[0].type !== 'object' &&
        schema.$_terms.items[0].type !== 'array'
      ) {
        // eslint-disable-line no-underscore-dangle
        return [field];
      }

      const tmpParameters = this.getStateParametersForField(
        '',
        schema.$_terms.items[0],
      ); // eslint-disable-line no-underscore-dangle

      for (let i = 0; i < 5; i++) {
        parameters = parameters.concat(
          Velcro.prefixStateParameters(`${field}.${i}`, tmpParameters),
        );
      }
    } else {
      parameters.push(field);
    }

    return parameters;
  }

  static prefixStateParameters(prefix, parameters) {
    if (prefix === '') {
      return parameters;
    }

    const newParameters = [];

    parameters.forEach((parameter) => {
      newParameters.push(`${prefix}.${parameter}`);
    });

    return newParameters;
  }
}
